import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';

const rowColor = (days) => {
  if (days == null) return {};
  const d = Number(days);
  if (d <= 0) return { background: '#fef2f2', color: '#991b1b' };
  if (d <= 30) return { background: '#fff7ed', color: '#92400e' };
  if (d <= 60) return { background: '#fefce8', color: '#713f12' };
  return {};
};

const statusBadge = (days) => {
  const d = Number(days);
  if (d <= 0) return <span className="badge badge-danger" style={{ fontSize: '0.7rem' }}>Expired</span>;
  if (d <= 30) return <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>Expiring Soon</span>;
  if (d <= 60) return <span style={{ fontSize: '0.7rem', padding: '2px 7px', borderRadius: 8, background: '#fefce8', color: '#713f12', border: '1px solid #fde68a', fontWeight: 600 }}>60 Days</span>;
  return <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>Valid</span>;
};

export default function LicenseExpiry() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  // Edit existing
  const [editModal, setEditModal] = useState({ open: false, row: null });
  const [editForm, setEditForm] = useState({ license_number: '', license_expiry_date: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Add new
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ entity_type: 'customer', entity_id: '', license_number: '', license_expiry_date: '' });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [clientList, setClientList] = useState([]);
  const [customerList, setCustomerList] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clients/license-expiry', { params: { days } });
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const expired = rows.filter(r => Number(r.days_remaining) <= 0).length;
  const exp30   = rows.filter(r => Number(r.days_remaining) > 0 && Number(r.days_remaining) <= 30).length;
  const exp90   = rows.filter(r => Number(r.days_remaining) > 0 && Number(r.days_remaining) <= 90).length;

  const openAdd = () => {
    if (clientList.length === 0) {
      api.get('/clients', { params: { limit: 500 } }).then(r => setClientList(r.data.data || [])).catch(() => {});
      api.get('/customers', { params: { limit: 500 } }).then(r => setCustomerList(r.data.data || r.data || [])).catch(() => {});
    }
    setAddForm({ entity_type: 'customer', entity_id: '', license_number: '', license_expiry_date: '' });
    setAddError('');
    setAddModal(true);
  };

  const handleAdd = async () => {
    if (!addForm.entity_id) { setAddError('Please select a client or customer.'); return; }
    if (!addForm.license_expiry_date) { setAddError('Expiry date is required.'); return; }
    setAddSaving(true); setAddError('');
    const endpoint = addForm.entity_type === 'client'
      ? `/clients/${addForm.entity_id}`
      : `/customers/${addForm.entity_id}`;
    try {
      await api.put(endpoint, {
        license_number: addForm.license_number || null,
        license_expiry_date: addForm.license_expiry_date,
      });
      setAddModal(false);
      load();
    } catch (err) {
      setAddError(err.response?.data?.message || 'Failed to save.');
    } finally { setAddSaving(false); }
  };

  const openEdit = (r) => {
    setEditForm({
      license_number: r.license_number || '',
      license_expiry_date: r.license_expiry_date ? r.license_expiry_date.slice(0, 10) : '',
    });
    setSaveError('');
    setEditModal({ open: true, row: r });
  };

  const handleSave = async () => {
    if (!editForm.license_expiry_date) { setSaveError('Expiry date is required.'); return; }
    setSaving(true); setSaveError('');
    const { row } = editModal;
    const endpoint = row.entity_type === 'client' ? `/clients/${row.id}` : `/customers/${row.id}`;
    try {
      await api.put(endpoint, {
        license_number: editForm.license_number || null,
        license_expiry_date: editForm.license_expiry_date,
      });
      setEditModal({ open: false, row: null });
      load();
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">License Expiry Tracker</h1>
          <p className="page-subtitle">Drug license expiry dates for clients and customers</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Show expiring in</label>
          <select className="form-control form-select" style={{ width: 120 }} value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
          </select>
          <button className="btn btn-primary" onClick={openAdd}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add License
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div className="card" style={{ padding: '14px 18px', borderLeft: '4px solid #dc2626' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Expired</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#dc2626', marginTop: 4 }}>{expired}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderLeft: '4px solid #f59e0b' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Expiring in 30 days</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#d97706', marginTop: 4 }}>{exp30}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Expiring in 90 days</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#2563eb', marginTop: 4 }}>{exp90}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderLeft: '4px solid #64748b' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Total Shown</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: 4 }}>{rows.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Entity</th>
                <th>License Number</th>
                <th>Expiry Date</th>
                <th>Days Remaining</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner"/></div></td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8}>
                  <div className="empty-state">
                    <p>No licenses expiring within {days} days.</p>
                  </div>
                </td></tr>
              )}
              {!loading && rows.map((r, i) => (
                <tr key={`${r.entity_type}-${r.id}-${i}`} style={rowColor(r.days_remaining)}>
                  <td style={{ fontWeight: 600 }}>{r.name}</td>
                  <td style={{ textTransform: 'capitalize', fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{r.type || '—'}</td>
                  <td>
                    <span className={`badge badge-${r.entity_type === 'client' ? 'primary' : 'info'}`} style={{ textTransform: 'capitalize', fontSize: '0.72rem' }}>
                      {r.entity_type}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.83rem' }}>{r.license_number || '—'}</td>
                  <td style={{ fontSize: '0.83rem' }}>{fmtDate(r.license_expiry_date)}</td>
                  <td style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {r.days_remaining <= 0
                      ? `${Math.abs(r.days_remaining)} days ago`
                      : `${r.days_remaining} days`}
                  </td>
                  <td>{statusBadge(r.days_remaining)}</td>
                  <td>
                    <button className="btn btn-sm btn-secondary" onClick={() => openEdit(r)}>
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add License Modal */}
      <Modal
        open={addModal}
        onClose={() => !addSaving && setAddModal(false)}
        title="Add License"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setAddModal(false)} disabled={addSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={addSaving}>
            {addSaving ? 'Saving…' : 'Save License'}
          </button>
        </>}
      >
        <div>
          <div className="form-group">
            <label className="form-label">Entity Type</label>
            <div style={{ display: 'flex', gap: 16 }}>
              {['customer', 'client'].map(t => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem', fontWeight: addForm.entity_type === t ? 600 : 400 }}>
                  <input type="radio" name="entity_type" value={t} checked={addForm.entity_type === t}
                    onChange={() => setAddForm(f => ({ ...f, entity_type: t, entity_id: '' }))} />
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{addForm.entity_type === 'client' ? 'Client' : 'Customer'} <span style={{ color: 'var(--error)' }}>*</span></label>
            <select className="form-control form-select" value={addForm.entity_id}
              onChange={e => setAddForm(f => ({ ...f, entity_id: e.target.value }))}>
              <option value="">— Select —</option>
              {(addForm.entity_type === 'client' ? clientList : customerList).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Drug License Number</label>
              <input className="form-control" value={addForm.license_number}
                onChange={e => setAddForm(f => ({ ...f, license_number: e.target.value }))}
                placeholder="e.g. DL-MH-12345" />
            </div>
            <div className="form-group">
              <label className="form-label">License Expiry Date <span style={{ color: 'var(--error)' }}>*</span></label>
              <input className="form-control" type="date" value={addForm.license_expiry_date}
                onChange={e => setAddForm(f => ({ ...f, license_expiry_date: e.target.value }))} />
            </div>
          </div>
          {addError && <div style={{ color: 'var(--error)', fontSize: '0.82rem', marginTop: 4 }}>{addError}</div>}
        </div>
      </Modal>

      {/* Edit License Modal */}
      <Modal
        open={editModal.open}
        onClose={() => !saving && setEditModal({ open: false, row: null })}
        title={editModal.row ? `Edit License — ${editModal.row.name}` : 'Edit License'}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setEditModal({ open: false, row: null })} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>}
      >
        {editModal.row && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <span className={`badge badge-${editModal.row.entity_type === 'client' ? 'primary' : 'info'}`} style={{ textTransform: 'capitalize' }}>
                {editModal.row.entity_type}
              </span>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{editModal.row.type}</span>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Drug License Number</label>
                <input
                  className="form-control"
                  value={editForm.license_number}
                  onChange={e => setEditForm(f => ({ ...f, license_number: e.target.value }))}
                  placeholder="e.g. DL-MH-12345"
                />
              </div>
              <div className="form-group">
                <label className="form-label">License Expiry Date <span style={{ color: 'var(--error)' }}>*</span></label>
                <input
                  className="form-control"
                  type="date"
                  value={editForm.license_expiry_date}
                  onChange={e => setEditForm(f => ({ ...f, license_expiry_date: e.target.value }))}
                />
              </div>
            </div>
            {saveError && <div style={{ color: 'var(--error)', fontSize: '0.82rem', marginTop: 8 }}>{saveError}</div>}
          </div>
        )}
      </Modal>
    </div>
  );
}
